import {Icon, IconName} from '@wandb/weave/components/Icon';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import React, {useContext} from 'react';

import {OLD_MODEL_REGISTRY_PROJECT_NAME} from '../../../util/artifacts';
import DocUrls from '../../../util/doc_urls';
import {
  Banner,
  BannerBoldText,
  BannerButton,
  BannerFlexWrapper,
  BannerTextWrapper,
} from '../../Banner';
import {BannerVariant} from '../../Banner/Banner.styles';
import {useIsModelRegistryMigrationNotificationEnabled} from './hooks';
import {ModelRegistryMigrationContext} from './ModelRegistryMigrationContextProvider';
import {MigrationStatus} from './utils';

const AllTeamsBannerText: React.FC<{status: MigrationStatus}> = ({status}) => {
  switch (status) {
    case MigrationStatus.IN_PROGRESS:
      return (
        <span>
          <BannerBoldText>
            Some of your team model registries are now read only.
          </BannerBoldText>{' '}
          We're migrating all content to a new W&B Registry, which will become
          available soon - no action is needed from you.
        </span>
      );
    case MigrationStatus.COMPLETED:
      return (
        <span>
          <BannerBoldText>
            Your team model registries have been migrated.
          </BannerBoldText>{' '}
          The legacy model registries and their collections were moved to a new
          W&B Registry, with the same access and settings.
        </span>
      );
    default:
      return (
        <span>
          <BannerBoldText>
            Heads up - We're migrating this registry.
          </BannerBoldText>{' '}
          On July 26, 2025 legacy model registries and their collections will be
          moved to new W&B registries, with the same access and settings. No
          action needed on your part.
        </span>
      );
  }
};

const SingleTeamBannerText: React.FC<{status: MigrationStatus}> = ({
  status,
}) => {
  switch (status) {
    case MigrationStatus.IN_PROGRESS:
      return (
        <span>
          <BannerBoldText>This Model registry is now read only.</BannerBoldText>{' '}
          We're migrating all content to a new W&B Registry, which will become
          available within 1 hour - no action is needed from you.
        </span>
      );
    case MigrationStatus.COMPLETED:
      return (
        <span>
          <BannerBoldText>
            This Model registry has been migrated.
          </BannerBoldText>{' '}
          This legacy model registry and its collections were moved to a new W&B
          Registry, with the same access and settings.
        </span>
      );
    default:
      return (
        <span>
          <BannerBoldText>
            Heads up - We're migrating this registry.
          </BannerBoldText>{' '}
          On July 26, 2025 this legacy model registry and its collections will
          be moved to a new W&B Registry, with the same access and settings. No
          action needed on your part.
        </span>
      );
  }
};

type MigrationStatusBannerProps = {
  entityName?: string;
  projectName?: string;
  className?: string;
};

export const MigrationStatusBanner = ({
  projectName,
  className,
}: MigrationStatusBannerProps) => {
  const {enableMRMigrationNotification} =
    useIsModelRegistryMigrationNotificationEnabled();
  const {
    entityMigrationStatus,
    linkToMigratedRegistry,
    linkToMigratedArtifactCollection,
  } = useContext(ModelRegistryMigrationContext);

  if (projectName !== OLD_MODEL_REGISTRY_PROJECT_NAME) {
    return null;
  }

  const isCompleted = entityMigrationStatus === MigrationStatus.COMPLETED;
  const isInProgress = entityMigrationStatus === MigrationStatus.IN_PROGRESS;

  const bannerVariant: BannerVariant = isCompleted ? 'info' : 'announcement';
  const bannerIconName: IconName = isCompleted ? 'lightbulb-info' : 'warning';

  let button = null;
  if (isCompleted) {
    const inCollectionView = linkToMigratedArtifactCollection != null;
    const buttonText = inCollectionView
      ? 'View migrated collection'
      : 'View migrated registry';
    const buttonLink = inCollectionView
      ? linkToMigratedArtifactCollection
      : linkToMigratedRegistry;

    button = (
      <BannerButton
        variant="info"
        text={buttonText}
        link={buttonLink}
        eventData={{}}
      />
    );
  } else if (!isInProgress && enableMRMigrationNotification) {
    button = (
      <BannerButton
        variant="warning"
        text="Learn more"
        link={DocUrls.registry + 'model_registry_eol'}
        eventData={{}}
      />
    );
  }

  // Lastly, we only show the pre-migration warning banner if the ramp flag is enabled for an organization
  if (
    entityMigrationStatus === MigrationStatus.NOT_STARTED &&
    !enableMRMigrationNotification
  ) {
    return null;
  }
  return (
    <Tailwind>
      <Banner className={className} variant={bannerVariant}>
        <BannerFlexWrapper>
          <div>
            <Icon name={bannerIconName} />
          </div>
          <BannerTextWrapper>
            <SingleTeamBannerText status={entityMigrationStatus} />
          </BannerTextWrapper>
        </BannerFlexWrapper>
        {button && <div className="ml-12 font-semibold">{button}</div>}
      </Banner>
    </Tailwind>
  );
};

// Since the all teams page depends on the organization's migration status,
// its status banner is separate from the singleTeamPage's banner and internal
// collection page banner.
export const AllTeamsMigrationStatusBanner = ({
  projectName,
  className,
}: MigrationStatusBannerProps) => {
  const {enableMRMigrationNotification} =
    useIsModelRegistryMigrationNotificationEnabled();
  const {orgMigrationStatus} = useContext(ModelRegistryMigrationContext);

  if (projectName !== OLD_MODEL_REGISTRY_PROJECT_NAME) {
    return null;
  }
  const isCompleted = orgMigrationStatus === MigrationStatus.COMPLETED;
  const bannerVariant: BannerVariant = isCompleted ? 'info' : 'announcement';
  const bannerIconName: IconName = isCompleted ? 'lightbulb-info' : 'warning';

  // Lastly, we only show the pre-migration warning banner if the ramp flag is enabled for an organization
  if (
    orgMigrationStatus === MigrationStatus.NOT_STARTED &&
    !enableMRMigrationNotification
  ) {
    return null;
  }
  return (
    <Tailwind>
      <Banner className={className} variant={bannerVariant}>
        <BannerFlexWrapper>
          <div>
            <Icon name={bannerIconName} />
          </div>
          <BannerTextWrapper>
            <AllTeamsBannerText status={orgMigrationStatus} />
          </BannerTextWrapper>
        </BannerFlexWrapper>
        {orgMigrationStatus === MigrationStatus.NOT_STARTED && (
          <div className="ml-12 font-semibold">
            <BannerButton
              variant="warning"
              text="Learn more"
              link={DocUrls.registry + 'model_registry_eol'}
              eventData={{}}
            />
          </div>
        )}
      </Banner>
    </Tailwind>
  );
};
